import React, { useEffect, useState } from 'react';
import { XIcon, BuildingIcon, ChevronDownIcon, UploadIcon, Trash2Icon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}
type TabType = 'legal' | 'address' | 'contact';
export function EditCompanyModal({
  isOpen,
  onClose,
  readOnly = false
}: EditCompanyModalProps) {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('legal');
      setLegalName('DAIRY GLOBAL EXPERTS LLC LN');
      setTaxId('INN 392949923233');
      setBillingCountry('');
      setLineAddress1('50 Centralna st');
      setLineAddress2('');
      setCity('Hromy village, Uman district');
      setStateProvince('Cherkasy Region');
      setZipCode('20343');
      setDistributionCountry('Ukraine');
      setPhone('+380 97 410 2423');
      setEmail('irrigation.dge@gmail.com');
      setUrl('www.dge.com.ua');
      setDealerMarkup('25.00');
      setLogoPreview(companyLogo);
    }
  }, [isOpen, companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    onClose();
  };
  if (!isOpen) return null;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'legal' as TabType,
    label: 'Legal Data'
  }, {
    id: 'address' as TabType,
    label: 'Address'
  }, {
    id: 'contact' as TabType,
    label: 'Contact'
  }];
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleBackdropClick} data-id="element-3162">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-2xl my-8 relative flex flex-col max-h-[90vh]" data-id="element-3163">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0" data-id="element-3164">
          <h2 className="text-white font-semibold text-lg" data-id="element-3165">
            {readOnly ? 'Company Information' : 'Edit Company Information'}
          </h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-3166">
            <XIcon size={20} data-id="element-3167" />
          </button>
        </div>

        {/* Company Header Section */}
        <div className="px-6 py-4 flex-shrink-0 border-b border-white/10" data-id="element-3168">
          <div className="flex items-center justify-between gap-4" data-id="element-3169">
            {/* Left side: Logo and Company Name */}
            <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-3170">
              {/* Logo Section */}
              <div className="relative flex-shrink-0" data-id="element-3171">
                {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-3172">
                    <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-3173" />
                  </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-3174">
                    <BuildingIcon size={28} className="text-blue-400" data-id="element-3175" />
                  </div>}

                {/* Logo Upload/Remove Controls */}
                {canEditLogo && !readOnly && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-3176">
                    <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-3177">
                      <UploadIcon size={10} data-id="element-3178" />
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-3179" />
                    </label>
                    {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-3180">
                        <Trash2Icon size={10} data-id="element-3181" />
                      </button>}
                  </div>}
              </div>

              {/* Company Name */}
              <div className="flex-1 min-w-0" data-id="element-3182">
                <h3 className="text-lg font-semibold text-white truncate" data-id="element-3183">
                  {companyName}
                </h3>
              </div>
            </div>

            {/* Right side: Distribution Country */}
            <div className="flex-shrink-0" data-id="element-3184">
              <p className="text-xs text-blue-300 mb-1" data-id="element-3185">
                Distribuidor oficial en
              </p>
              {canEditOfficialCountry && !readOnly ? <div className="relative" data-id="element-3186">
                  <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3187">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3188">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-3189">
                    <ChevronDownIcon size={14} data-id="element-3190" />
                  </div>
                </div> : <p className="text-sm font-medium text-white" data-id="element-3191">
                  {distributionCountry || 'No country set'}
                </p>}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 px-6 flex-shrink-0" data-id="element-3192">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-3193">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-3194" />}
            </button>)}
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar" data-id="element-3195">
          {/* Legal Data Tab */}
          {activeTab === 'legal' && <div className="space-y-5" data-id="element-3196">
              <div data-id="element-3197">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3198">
                  Legal name <span className="text-red-400" data-id="element-3199">*</span>
                </label>
                <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Company legal name" data-id="element-3200" />
              </div>

              <div data-id="element-3201">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3202">
                  TAX ID / VAT ID / NIF <span className="text-red-400" data-id="element-3203">*</span>
                </label>
                <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Tax identification number" data-id="element-3204" />
              </div>

              <div data-id="element-3205">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3206">
                  Billing country <span className="text-red-400" data-id="element-3207">*</span>
                </label>
                <div className="relative" data-id="element-3208">
                  <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} disabled={readOnly} className={`w-full appearance-none border rounded-lg px-4 py-2.5 text-white focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-3209">
                    <option value="" className="bg-slate-800 text-blue-300" data-id="element-3210">
                      Please, select country
                    </option>
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3211">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3212">
                    <ChevronDownIcon size={16} data-id="element-3213" />
                  </div>
                </div>
                {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-3214">
                    Please, select country
                  </p>}
              </div>
            </div>}

          {/* Billing Address Tab */}
          {activeTab === 'address' && <div className="space-y-5" data-id="element-3215">
              <div data-id="element-3216">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3217">
                  Address line 1 <span className="text-red-400" data-id="element-3218">*</span>
                </label>
                <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Street, number" data-id="element-3219" />
              </div>

              <div data-id="element-3220">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3221">
                  Address line 2
                </label>
                <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Apartment, suite, etc. (optional)" data-id="element-3222" />
              </div>

              <div data-id="element-3223">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3224">
                  City <span className="text-red-400" data-id="element-3225">*</span>
                </label>
                <input type="text" value={city} onChange={e => setCity(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="City" data-id="element-3226" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3227">
                <div data-id="element-3228">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3229">
                    State / Province
                  </label>
                  <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="State or province" data-id="element-3230" />
                </div>

                <div data-id="element-3231">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3232">
                    Zip code
                  </label>
                  <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Zip code" data-id="element-3233" />
                </div>
              </div>

              <div data-id="element-3234">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3235">
                  Distribution country <span className="text-red-400" data-id="element-3236">*</span>
                </label>
                <div className="relative" data-id="element-3237">
                  <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} disabled={readOnly} className={`w-full appearance-none border rounded-lg px-4 py-2.5 text-white focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-3238">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3239">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3240">
                    <ChevronDownIcon size={16} data-id="element-3241" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Contact Tab */}
          {activeTab === 'contact' && <div className="space-y-5" data-id="element-3242">
              <div data-id="element-3243">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3244">
                  Phone
                </label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="+1 234 567 8900" data-id="element-3245" />
              </div>

              <div data-id="element-3246">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3247">
                  Email
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="company@email.com" data-id="element-3248" />
              </div>

              <div data-id="element-3249">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3250">
                  Website
                </label>
                <input type="url" value={url} onChange={e => setUrl(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="www.company.com" data-id="element-3251" />
              </div>

              <div data-id="element-3252">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3253">
                  Dealer markup <span className="text-red-400" data-id="element-3254">*</span>
                </label>
                <div className="relative" data-id="element-3255">
                  <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 pr-12 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="25.00" data-id="element-3256" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-3257">
                    %
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end space-x-3 flex-shrink-0 bg-black/20 rounded-b-2xl" data-id="element-3258">
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors" data-id="element-3259">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-3260">
              Save changes
            </button>}
        </div>
      </div>
    </div>;
}